import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const products = [
  {
    id: "1g-double-eagle",
    name: "1 Gram Double Eagle",
    weight: "1 Gram Fine Gold 9999",
    images: {
      obverse: "/1g-double-eagle-reverse.png",
      reverse: "/1g-double-eagle-reverse-new.png",
    },
    description: "Our flagship gold note featuring the iconic Double Eagle design. This 1-gram note captures the timeless beauty of American numismatic history in a modern, portable polymer note format with advanced security features.",
    features: [
      "24-Karat Pure Gold (9999 Fineness)",
      "1 Gram Net Weight",
      "Iconic Double Eagle Design",
      "U.S. Patent No. 10,668,765",
      "Secure QR Authentication",
      "Certificate of Authenticity",
      "Protective Sleeve Included"
    ],
    status: "available",
  },
  {
    id: "electrum",
    name: "Electrum",
    weight: "1 Gram Fine Gold 9999",
    images: {
      obverse: "/electrum-obverse.png",
      reverse: "/electrum-reverse.png",
    },
    description: "The Electrum gold note pays homage to the ancient alloy of gold and silver that gave birth to the world's first coins. This stunning design blends classical elegance with modern craftsmanship, featuring intricate detailing and advanced security features.",
    features: [
      "24-Karat Pure Gold (9999 Fineness)",
      "1 Gram Net Weight",
      "Classical Electrum Design",
      "U.S. Patent No. 10,668,765",
      "Secure QR Authentication",
      "Certificate of Authenticity",
      "Protective Sleeve Included"
    ],
    status: "available",
  },
  {
    id: "year-of-horse",
    name: "Year of the Horse",
    weight: "1 Gram Fine Gold 9999",
    images: {
      obverse: "/year-of-horse-obverse.png",
      reverse: "/year-of-horse-reverse.png",
    },
    description: "Celebrate the Chinese Zodiac with this exquisite Year of the Horse gold note. The horse symbolizes success, loyalty, and freedom in Chinese culture. This limited edition note features stunning oriental artistry combined with premium gold craftsmanship.",
    features: [
      "24-Karat Pure Gold (9999 Fineness)",
      "1 Gram Net Weight",
      "Chinese Zodiac Horse Design",
      "U.S. Patent No. 10,668,765",
      "Secure QR Authentication",
      "Certificate of Authenticity",
      "Protective Sleeve Included"
    ],
    status: "available",
  },
];

// Magnifiable Image Component for Detail View
const MagnifiableImage = ({ src, alt }) => {
  const [magnifyStyle, setMagnifyStyle] = useState({ display: 'none' });
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const xPercent = (x / rect.width) * 100;
    const yPercent = (y / rect.height) * 100;
    
    setMagnifyStyle({
      display: 'block',
      left: `${x}px`,
      top: `${y}px`,
      backgroundPosition: `${xPercent}% ${yPercent}%`,
    });
  };

  return (
    <div 
      className="magnifiable-image-container"
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false);
        setMagnifyStyle({ display: 'none' });
      }}
    >
      <img src={src} alt={alt} className="magnifiable-image" draggable="false" />
      {isHovering && (
        <div 
          className="magnifier-detail"
          style={{
            ...magnifyStyle,
            backgroundImage: `url(${src})`,
          }}
        />
      )}
    </div>
  );
};

// Flip Card Component with Hover-to-Flip (no magnify on main card)
const FlipCard = ({ product, onViewDetails }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const isComingSoon = product.status === "coming-soon";

  return (
    <div 
      className="flip-card-container group"
      data-testid={`product-card-${product.id}`}
    >
      {/* Display Case Frame */}
      <div 
        className={`display-case ${isComingSoon ? 'opacity-80' : ''}`}
        onMouseEnter={() => !isComingSoon && setIsFlipped(true)}
        onMouseLeave={() => setIsFlipped(false)}
      >
        {/* Coming Soon Badge */}
        {isComingSoon && (
          <div className="absolute top-3 right-3 z-30 px-3 py-1.5 bg-[#D4AF37] text-black text-[0.6rem] tracking-[0.15em] uppercase font-medium rounded">
            Coming Soon
          </div>
        )}
        
        {/* Category Badge */}
        {product.category && (
          <div className="absolute top-3 left-3 z-30 px-3 py-1.5 bg-black/70 border border-[#D4AF37]/40 text-[#D4AF37] text-[0.55rem] tracking-[0.15em] uppercase rounded">
            {product.category}
          </div>
        )}
        
        {/* Glass Reflection Effect */}
        <div className="glass-reflection" />
        
        {/* Spotlight Effect */}
        <div className="spotlight" />
        
        {/* The Flip Card */}
        <div className={`flip-card ${isFlipped ? 'flipped' : ''}`}>
          {/* Front - Obverse */}
          <div className="flip-card-front">
            <div className="card-inner">
              <img
                src={product.images.obverse}
                alt={`${product.name} - Obverse`}
                className={`product-image ${isComingSoon ? 'grayscale-[30%]' : ''}`}
                draggable="false"
              />
            </div>
          </div>
          
          {/* Back - Reverse */}
          <div className="flip-card-back">
            <div className="card-inner">
              <img
                src={product.images.reverse}
                alt={`${product.name} - Reverse`}
                className={`product-image ${isComingSoon ? 'grayscale-[30%]' : ''}`}
                draggable="false"
              />
            </div>
          </div>
        </div>
        
        {/* Hover to Flip Indicator */}
        {!isComingSoon && (
          <div className="flip-indicator">
            <span className="flip-text">Hover to flip</span>
            <svg className="flip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        )}
      </div>
      
      {/* Product Info */}
      <div className="product-info">
        <p className="product-weight">{product.weight}</p>
        <h3 className="product-name">{product.name}</h3>
        {isComingSoon ? (
          <p className="text-[#D4AF37]/60 text-xs tracking-wider uppercase mt-2">Notify Me When Available</p>
        ) : (
          <button className="view-details-btn" onClick={onViewDetails}>
            <span>View Details</span>
            <svg className="arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export const Products = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="products-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-[0.625rem] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-6 animate-fade-in">
              The Collection
            </p>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[0.9] mb-6 animate-fade-in-up">
              <span className="gold-gradient">Products</span>
            </h1>
            <div className="w-16 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto mb-6" />
            <p className="text-base md:text-lg text-white/60 font-light leading-relaxed animate-fade-in-up-delay-1">
              Museum-quality gold notes, meticulously crafted for collectors and investors.
              <br className="hidden md:block" />
              Hover to flip. Click View Details to magnify.
            </p>
          </div>
        </div>
      </section>

      {/* Products Display Gallery */}
      <section className="pb-24" data-testid="products-grid">
        <div className="container mx-auto px-6">
          <div className="products-gallery">
            {products.map((product) => (
              <FlipCard 
                key={product.id}
                product={product}
                onViewDetails={() => setSelectedProduct(product)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Product Detail Dialog */}
      <Dialog open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
        <DialogContent 
          className="bg-[#0A0A0A] border border-[#D4AF37]/20 max-w-4xl max-h-[90vh] overflow-y-auto"
          data-testid="product-dialog"
        >
          {selectedProduct && (
            <>
              <DialogHeader>
                <p className="text-[0.625rem] tracking-[0.2em] uppercase text-[#D4AF37]/80 mb-2">
                  {selectedProduct.weight}
                </p>
                <DialogTitle className="font-serif text-2xl md:text-3xl text-white/90">
                  {selectedProduct.name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="mt-6">
                {/* Large Product Images with Magnify */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="detail-image-card">
                    <p className="detail-image-label">Obverse</p>
                    <MagnifiableImage 
                      src={selectedProduct.images.obverse} 
                      alt={`${selectedProduct.name} - Obverse`}
                    />
                  </div>
                  <div className="detail-image-card">
                    <p className="detail-image-label">Reverse</p>
                    <MagnifiableImage 
                      src={selectedProduct.images.reverse} 
                      alt={`${selectedProduct.name} - Reverse`}
                    />
                  </div>
                </div>
                
                <p className="text-[0.625rem] tracking-[0.15em] uppercase text-[#D4AF37]/60 text-center mb-6">
                  Hover over images to magnify details
                </p>
                
                <DialogDescription className="text-white/70 font-light leading-relaxed mb-6">
                  {selectedProduct.description}
                </DialogDescription>

                <div>
                  <h4 className="text-sm tracking-wider uppercase text-[#D4AF37] mb-4">Features</h4>
                  <ul className="space-y-2">
                    {selectedProduct.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3 text-sm text-white/60">
                        <span className="w-1.5 h-1.5 bg-[#D4AF37]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Coming Soon Section */}
      <section className="py-20 bg-[#0A0A0A]" data-testid="coming-soon-section">
        <div className="container mx-auto px-6 text-center">
          <p className="text-[0.625rem] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-4">
            Expanding Our Collection
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-white/90 mb-6">
            More Products Coming Soon
          </h2>
          <p className="text-white/60 font-light max-w-xl mx-auto">
            We are continuously developing new gold note designs to expand our collection. 
            Stay tuned for upcoming releases and limited editions.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Products;
